import { id } from "@instantdb/react";
import { signal } from "@preact/signals";
import { empty, map } from "gamla";
import { useRef } from "preact/hooks";
import { toast } from "react-hot-toast";
import { apiClient, maxFileSizeBytes } from "../../constants.ts";
import { BotNotFound, Button, PageSpinner, Spinner } from "./components.tsx";
import { storage, transact, tx, useAuth, useQuery } from "./db.ts";
import { EditableText } from "./editableText.tsx";

const IconFilePdf = ({ size, color }: { size: number; color: string }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 384 512"
    fill={color}
    aria-label="PDF file"
  >
    <path d="M181.9 256.1c-5-16-4.9-46.9-2-46.9 8.4 0 7.6 36.9 2 46.9zm-1.7 47.2c-7.7 20.2-17.3 43.3-28.4 62.7 18.3-7 39-17.2 62.9-21.9-12.7-9.6-24.9-23.4-34.5-40.8zM86.1 428.1c0 .8 13.2-5.4 34.9-40.2-6.7 6.3-29.1 24.5-34.9 40.2zM248 160h136v328c0 13.3-10.7 24-24 24H24c-13.3 0-24-10.7-24-24V24C0 10.7 10.7 0 24 0h200v136c0 13.2 10.8 24 24 24zm-8 171.8c-20-12.2-33.3-29-42.7-53.8 4.5-18.5 11.6-46.6 6.2-64.2-4.7-29.4-42.4-26.5-47.8-6.8-5 18.3-.4 44.1 8.1 77-11.6 27.6-28.7 64.6-40.8 85.8-.1 0-.1.1-.2.1-27.1 13.9-73.6 44.5-54.5 68 5.6 6.9 16 10 21.5 10 17.9 0 35.7-18 61.1-61.8 25.8-8.5 54.1-19.1 79-23.2 21.7 11.8 47.1 19.5 64 19.5 29.2 0 31.2-32 19.7-43.4-13.9-13.6-54.3-9.7-73.6-7.2zM377 105L279 7c-4.5-4.5-10.6-7-17-7h-6v128h128v-6.1c0-6.3-2.5-12.4-7-16.9zm-74.1 255.3c4.1-2.7-2.5-11.9-42.8-9 37.1 15.8 42.8 9 42.8 9z" />
  </svg>
);

const IconCheckCircle = ({ size, color }: { size: number; color: string }) => (
  <svg width={size} height={size} viewBox="0 0 512 512" fill={color}>
    <path d="M504 256c0 136.967-111.033 248-248 248S8 392.967 8 256 119.033 8 256 8s248 111.033 248 248zM227.314 387.314l184-184c6.248-6.248 6.248-16.379 0-22.627l-22.627-22.627c-6.248-6.249-16.379-6.249-22.628 0L216 308.118l-70.059-70.059c-6.248-6.248-16.379-6.248-22.628 0l-22.627 22.627c-6.248 6.248-6.248 16.379 0 22.627l104 104c6.249 6.249 16.379 6.249 22.628.001z" />
  </svg>
);

const IconTrash = ({ size, color }: { size: number; color: string }) => (
  <svg width={size} height={size} viewBox="0 0 448 512" fill={color}>
    <path d="M432 32H312l-9.4-18.7A24 24 0 0 0 281.1 0H166.8a23.72 23.72 0 0 0-21.4 13.3L136 32H16A16 16 0 0 0 0 48v32a16 16 0 0 0 16 16h416a16 16 0 0 0 16-16V48a16 16 0 0 0-16-16zM53.2 467a48 48 0 0 0 47.9 45h245.8a48 48 0 0 0 47.9-45L416 128H32z" />
  </svg>
);

const hashFile = async (file: File) => {
  const arrayBuffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest("SHA-256", arrayBuffer);
  return Array.from(new Uint8Array(hashBuffer)).map((b) =>
    b.toString(16).padStart(2, "0")
  ).join("");
};

const encodeRFC5987ValueChars = (str: string) =>
  encodeURIComponent(str)
    .replace(/'/g, "%27")
    .replace(/\(/g, "%28")
    .replace(/\)/g, "%29")
    .replace(/\*/g, "%2A");

const indexFile = (newId: string, token: string) =>
  apiClient({
    endpoint: "index-file",
    token,
    payload: { fileId: newId },
  });

const handleSingleFile =
  (token: string, folderId: string) => async (file: File) => {
    if (file.size > maxFileSizeBytes) {
      throw new Error(
        `File "${file.name}" exceeds the ${
          maxFileSizeBytes / 1024 / 1024
        }MB size limit`,
      );
    }
    console.log(`[handleSingleFile] Hashing ${file.name}`);
    const hash = await hashFile(file);
    console.log(
      `[handleSingleFile] Uploading ${file.name} (${file.size} bytes)`,
    );
    const encodedName = encodeRFC5987ValueChars(file.name);
    await storage.uploadFile(hash, file, {
      contentDisposition: `inline; filename*=UTF-8''${encodedName}`,
      contentType: file.type,
    });
    console.log(`[handleSingleFile] Creating file record for ${file.name}`);
    const newId = id();
    await transact([
      tx.files[newId].update({
        hash,
        name: file.name,
        type: file.type,
      }),
      tx.folders[folderId].link({ files: [newId] }),
    ]);
    console.log(`[handleSingleFile] Indexing ${file.name}`);
    await indexFile(newId, token);
    console.log(`[handleSingleFile] Done ${file.name}`);
    return newId;
  };

const handleFiles = async (token: string, id: string, files: File[]) => {
  await map(handleSingleFile(token, id))(files);
};

const addFiles = (
  token: string,
  id: string,
  setUploading: (v: boolean) => void,
  fileInputRef: React.RefObject<HTMLInputElement | null>,
) =>
async ({ target }: Event) => {
  const input = target as HTMLInputElement;
  if (!input.files || !input.files[0]) return;
  setUploading(true);
  await toast.promise(handleFiles(token, id, Array.from(input.files)), {
    loading: "Uploading files...",
    success: "Files uploaded successfully",
    error: (err) =>
      `Error uploading files: ${err.message || JSON.stringify(err)}`,
  }).catch(() => {});
  if (fileInputRef.current) fileInputRef.current.value = "";
  setUploading(false);
};

const ragIndexBoxStyle =
  "bg-surface-raised-light dark:bg-surface-raised rounded-lg shadow-md p-6 mb-8 border border-border-subtle-light dark:border-border-subtle";
const ragIndexHeaderStyle = "flex items-center justify-between mb-2";
const ragIndexLabelInputStyle =
  "text-xl font-semibold border-b border-border-default-light dark:border-border-default focus:outline-none focus:border-accent bg-transparent px-1 py-0.5 text-text-primary-light dark:text-text-primary";
const ragIndexLabelStyle =
  "text-xl font-semibold cursor-pointer hover:underline text-text-primary-light dark:text-text-primary";
const fileListItemStyle =
  "flex items-center gap-2 py-1 border-b last:border-b-0 border-border-subtle-light dark:border-border-subtle";
const fileListEmptyStyle = "text-text-tertiary-light dark:text-text-tertiary";
const addFilesLabelStyle =
  "flex items-center px-3 py-1.5 bg-accent/10 dark:bg-accent/20 border border-accent/40 text-accent-light dark:text-accent rounded cursor-pointer hover:bg-accent/20 dark:hover:bg-accent/30 transition-colors duration-150 font-medium shadow-sm";

const IconFileText = ({ size, color }: { size: number; color: string }) => (
  <svg width={size} height={size} viewBox="0 0 384 512" fill={color}>
    <path d="M224 136V0H24C10.7 0 0 10.7 0 24v464c0 13.3 10.7 24 24 24h336c13.3 0 24-10.7 24-24V160H248c-13.2 0-24-10.8-24-24zm64 236c0 6.6-5.4 12-12 12H108c-6.6 0-12-5.4-12-12v-8c0-6.6 5.4-12 12-12h168c6.6 0 12 5.4 12 12v8zm0-64c0 6.6-5.4 12-12 12H108c-6.6 0-12-5.4-12-12v-8c0-6.6 5.4-12 12-12h168c6.6 0 12 5.4 12 12v8zm0-72v8c0 6.6-5.4 12-12 12H108c-6.6 0-12-5.4-12-12v-8c0-6.6 5.4-12 12-12h168c6.6 0 12 5.4 12 12zm96-114.1v6.1H256V0h6.1c6.4 0 12.5 2.5 17 7l97.9 98c4.5 4.5 7 10.6 7 16.9z" />
  </svg>
);

const FileIcon = ({ name }: { name: string }) =>
  name.endsWith(".pdf")
    ? <IconFilePdf size={20} color="#dc2626" />
    : <IconFileText size={20} color="#6b7280" />;

const FileView = ({ id }: { id: string }) => {
  const { data, error, isLoading } = useQuery({
    files: { ragDocuments: {}, $: { where: { id } } },
  });
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-2">
        <Spinner className="h-4 w-4" />
      </div>
    );
  }
  if (error) return <div>Error: {error.message}</div>;
  if (empty(data.files)) return <div>File not found</div>;
  const [file] = data.files;
  const token = useAuth().user?.refresh_token;
  if (!token) return <div>Not authenticated</div>;
  const { name } = file;
  const { data: rawFiles, isLoading: isLoadingFile } = useQuery({
    $files: { $: { where: { path: file.hash } } },
  });
  if (isLoadingFile) {
    return (
      <div className="flex items-center justify-center py-2">
        <Spinner className="h-4 w-4" />
      </div>
    );
  }
  if (!rawFiles?.$files || rawFiles.$files.length === 0) {
    return <div>File not found</div>;
  }
  return (
    <div className={fileListItemStyle}>
      <span style={{ display: "flex", alignItems: "center" }}>
        <FileIcon name={name} />
      </span>
      <span className="truncate max-w-xs" title={name}>
        <a
          href={rawFiles.$files[0].url}
          download={name}
          className="text-accent-light dark:text-accent underline cursor-pointer"
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          {name}
        </a>
      </span>
      <span
        style={{ display: "flex", alignItems: "center", marginLeft: "8px" }}
      >
        {empty(file.ragDocuments)
          ? (
            <span title="Indexing...">
              <Spinner />
            </span>
          )
          : (
            <span title="Indexed">
              <IconCheckCircle color="#16a34a" size={18} />
            </span>
          )}
      </span>
      <span>{file.ragDocuments.length} parts</span>
      <Button
        type="button"
        onClick={() => {
          toast.promise(
            apiClient({
              token,
              endpoint: "remove-file",
              payload: { fileId: id },
            }),
            {
              loading: "Removing file...",
              success: "File removed",
              error: (err) => `Error removing file: ${err.message || err}`,
            },
          );
        }}
        disabled={uploading.value}
        aria-label="Remove file"
        style={{ background: "none", padding: 0, minWidth: 0 }}
      >
        <span style={{ display: "flex", alignItems: "center" }}>
          <IconTrash size={20} color="currentColor" />
        </span>
      </Button>
    </div>
  );
};

const uploading = signal(false);

export const RagIndex = ({ id }: { id: string }) => {
  const { data, error, isLoading } = useQuery({
    folders: { files: { ragDocuments: {} }, $: { where: { id } } },
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const token = useAuth().user?.refresh_token;
  if (!token) return <div>Not authenticated</div>;
  if (isLoading) return <PageSpinner />;
  if (error) return <div>Error: {error.message}</div>;
  if (empty(data.folders)) return <div>Rag index not found</div>;
  const [ragIndex] = data.folders;
  const { files, name } = ragIndex;
  const indexUpdating = files.some((file: { ragDocuments: unknown[] }) =>
    empty(file.ragDocuments)
  );
  return (
    <div className={ragIndexBoxStyle}>
      <div className={ragIndexHeaderStyle}>
        <div className="flex items-center gap-2" style={{ flex: 1 }}>
          <EditableText
            value={name}
            onSave={async (newValue) => {
              if (newValue !== name) {
                await toast.promise(
                  transact(tx.folders[id].update({ name: newValue })),
                  {
                    loading: "Updating name...",
                    success: "Name updated",
                    error: (err) =>
                      `Error updating name: ${err.message || err}`,
                  },
                );
              }
            }}
            className="flex items-center gap-2"
            inputClassName={ragIndexLabelInputStyle}
            maxLength={40}
            style={undefined}
            inputStyle={{ minWidth: 80 }}
            placeholder="(No name)"
          >
            <h2
              className={ragIndexLabelStyle}
              title="Click to edit name"
              style={name ? undefined : { fontStyle: "italic" }}
            >
              {name || "(No name)"}
            </h2>
          </EditableText>
          {indexUpdating && (
            <span className="flex items-center gap-2 animate-pulse text-accent-light dark:text-accent">
              <Spinner />
              <span>Indexing files...</span>
            </span>
          )}
        </div>
        <Button
          type="button"
          disabled={indexUpdating}
          onClick={() =>
            toast.promise(
              apiClient({
                endpoint: "delete-folder",
                token,
                payload: { folderId: id },
              }),
              {
                loading: "Deleting folder...",
                success: "Folder deleted",
                error: (err) => `Error deleting folder: ${err.message || err}`,
              },
            )}
          className="bg-danger hover:bg-danger-hover text-white font-bold"
        >
          Delete folder
        </Button>
      </div>
      <div className="mb-4">
        {empty(files) && (
          <div className={fileListEmptyStyle}>No files added yet.</div>
        )}
        <div>
          {files.map(({ id }: { id: string }) => <FileView key={id} id={id} />)}
        </div>
      </div>
      <div className="flex items-center gap-2 mt-2">
        <label
          htmlFor={`file-upload-${id}`}
          className={addFilesLabelStyle}
        >
          <svg
            className="mr-2"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path d="M12 4v16m8-8H4" />
          </svg>
          {uploading.value ? <Spinner /> : "Add Files"}
          <input
            id={`file-upload-${id}`}
            type="file"
            ref={fileInputRef}
            onChange={addFiles(token, id, (v) => {
              uploading.value = v;
            }, fileInputRef)}
            disabled={uploading}
            multiple
            accept=".pdf,.txt,.md,.csv"
            className="hidden"
          />
        </label>
      </div>
    </div>
  );
};

const createNewIndex = (botId: string) => () =>
  toast.promise(
    transact(
      tx.folders[id()].update({
        pineconeName: crypto.randomUUID(),
        name: "My new folder",
      }).link({
        bots: botId,
      }),
    ),
    {
      loading: "Creating new folder...",
      success: "Folder created",
      error: (err) =>
        `Error creating folder: ${err.message || JSON.stringify(err)}`,
    },
  );

export const Files = ({ id }: { id: string }) => {
  const { data, error, isLoading } = useQuery({
    bots: { folders: {}, $: { where: { id } } },
  });
  if (isLoading) return <PageSpinner />;
  if (error) return <div>Error: {error.message}</div>;
  if (empty(data.bots)) return <BotNotFound />;
  const [bot] = data.bots;
  const folders = bot.folders ?? [];
  return (
    <div className="flex flex-col">
      {folders.length === 0 && (
        <div>
          No folders created yet. Create a new one to add files.
        </div>
      )}
      <div className="flex flex-col gap-8">
        {folders.map(({ id }: { id: string }) => <RagIndex key={id} id={id} />)}
      </div>
      <div>
        <Button
          type="button"
          onClick={createNewIndex(bot.id)}
          disabled={isLoading}
        >
          Create new folder
        </Button>
      </div>
    </div>
  );
};
